import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, User, Phone, Mail, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';

const ContactFormModal = ({ isOpen, onClose, onSubmitSuccess, variant = 'default' }) => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        reason: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^\+?[0-9]{7,15}$/.test(formData.phone.replace(/[\s-]/g, ''))) {
            newErrors.phone = 'Please enter a valid phone number';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.reason.trim()) {
            newErrors.reason = 'Reason to connect is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const sendToGoogleSheets = async (data) => {
        const GOOGLE_SHEETS_URL = process.env.REACT_APP_GOOGLE_SHEETS_URL;

        if (!GOOGLE_SHEETS_URL) {
            console.warn('Google Sheets URL not configured');
            return;
        }

        try {
            const response = await fetch(GOOGLE_SHEETS_URL, {
                method: 'POST',
                mode: 'no-cors',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: data.name,
                    phone: data.phone,
                    email: data.email,
                    reason: data.reason,
                    timestamp: new Date().toISOString()
                })
            });

            console.log('Data sent to Google Sheets');
        } catch (error) {
            console.error('Error sending to Google Sheets:', error);
        }
    };

    const sendEmail = async (data) => {
        const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
        const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
        const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

        if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
            console.warn('EmailJS credentials not configured');
            return;
        }

        try {
            const now = new Date();
            const submissionDate = now.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
            const submissionTime = now.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });

            await emailjs.send(
                SERVICE_ID,
                TEMPLATE_ID,
                {
                    from_name: data.name,
                    from_email: data.email,
                    phone: data.phone,
                    message: data.reason,
                    to_email: 'support@visist.ai',
                    submission_date: submissionDate,
                    submission_time: submissionTime
                },
                PUBLIC_KEY
            );
            console.log('Email sent successfully');
        } catch (error) {
            console.error('Error sending email:', error);
            throw error;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Send to both Google Sheets and Email
            await Promise.all([
                sendToGoogleSheets(formData),
                sendEmail(formData)
            ]);

            setSubmitStatus('success');
            if (onSubmitSuccess) onSubmitSuccess();

            // Reset form after 2 seconds
            setTimeout(() => {
                setFormData({ name: '', phone: '', email: '', reason: '' });
                setSubmitStatus(null);
                onClose();
            }, 2000);

        } catch (error) {
            console.error('Submission error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setFormData({ name: '', phone: '', email: '', reason: '' });
            setErrors({});
            setSubmitStatus(null);
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
                    />

                    {/* Modal */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ type: "spring", duration: 0.5 }}
                            className="relative w-full max-w-2xl pointer-events-auto"
                        >
                            {/* Form Container */}
                            <div className="relative bg-black border-1 border-emerald rounded-3xl shadow-2xl overflow-hidden">
                                {/* Decorative Elements */}
                                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                                <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

                                {/* Top Accent Line */}
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald via-neon-lime to-emerald"></div>

                                {/* Close Button */}
                                <button
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                    className="absolute top-6 right-6 z-50 p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-400 hover:text-white transition-all duration-300 disabled:opacity-50"
                                >
                                    <X className="w-5 h-5" />
                                </button>

                                {/* Content */}
                                <div className="relative z-10 p-8 md:p-12">
                                    {/* Header */}
                                    <div className="text-center mb-8">
                                        <motion.div
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ delay: 0.2, type: "spring" }}
                                            className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald/20 to-emerald/10 rounded-2xl mb-4"
                                        >
                                            {variant === 'cancellation' ? (
                                                <div className="text-3xl">😔</div>
                                            ) : (
                                                <Phone className="w-8 h-8 text-emerald" />
                                            )}
                                        </motion.div>
                                        <h2 className="text-3xl md:text-4xl font-black text-white mb-3 uppercase tracking-tight">
                                            {variant === 'cancellation' ? (
                                                <>We are <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-400">Sorry</span> you're leaving</>
                                            ) : (
                                                <>Let's <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Connect</span></>
                                            )}
                                        </h2>
                                        <p className="text-gray-400 text-lg px-4">
                                            {variant === 'cancellation' 
                                                ? "We'd love to know what we can do better before you go" 
                                                : "Tell us about your needs and we'll get back to you soon"}
                                        </p>
                                    </div>

                                    {/* Form */}
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        {/* Name Field */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-300 mb-2 uppercase tracking-wider">
                                                {variant === 'cancellation' ? 'Full Name *' : 'Name *'}
                                            </label>
                                            <div className="relative">
                                                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    placeholder="Your full name"
                                                    className={`w-full pl-12 pr-4 py-3 bg-white/5 border ${errors.name ? 'border-red-500' : 'border-white/10'} rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-emerald focus:ring-2 focus:ring-emerald/20 transition-all`}
                                                />
                                            </div>
                                            {errors.name && (
                                                <p className="mt-1 text-sm text-red-400 flex items-center gap-1">
                                                    <AlertCircle className="w-4 h-4" />
                                                    {errors.name}
                                                </p>
                                            )}
                                        </div>

                                        {/* Phone Field */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-300 mb-2 uppercase tracking-wider">
                                                Phone Number *
                                            </label>
                                            <div className="relative">
                                                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    placeholder="Your phone number"
                                                    className={`w-full pl-12 pr-4 py-3 bg-white/5 border ${errors.phone ? 'border-red-500' : 'border-white/10'} rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-emerald focus:ring-2 focus:ring-emerald/20 transition-all`}
                                                />
                                            </div>
                                            {errors.phone && (
                                                <p className="mt-1 text-sm text-red-400 flex items-center gap-1">
                                                    <AlertCircle className="w-4 h-4" />
                                                    {errors.phone}
                                                </p>
                                            )}
                                        </div>

                                        {/* Email Field */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-300 mb-2 uppercase tracking-wider">
                                                {variant === 'cancellation' ? 'Email ID *' : 'Email Address *'}
                                            </label>
                                            <div className="relative">
                                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    placeholder="your.email@example.com"
                                                    className={`w-full pl-12 pr-4 py-3 bg-white/5 border ${errors.email ? 'border-red-500' : 'border-white/10'} rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-emerald focus:ring-2 focus:ring-emerald/20 transition-all`}
                                                />
                                            </div>
                                            {errors.email && (
                                                <p className="mt-1 text-sm text-red-400 flex items-center gap-1">
                                                    <AlertCircle className="w-4 h-4" />
                                                    {errors.email}
                                                </p>
                                            )}
                                        </div>

                                        {/* Reason Field */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-300 mb-2 uppercase tracking-wider">
                                                {variant === 'cancellation' ? 'Reason for Cancelling the Subscription *' : 'Reason to Connect *'}
                                            </label>
                                            <div className="relative">
                                                <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-gray-500" />
                                                <textarea
                                                    name="reason"
                                                    value={formData.reason}
                                                    onChange={handleChange}
                                                    placeholder="Tell us how we can help you..."
                                                    rows="4"
                                                    className={`w-full pl-12 pr-4 py-3 bg-white/5 border ${errors.reason ? 'border-red-500' : 'border-white/10'} rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-emerald focus:ring-2 focus:ring-emerald/20 transition-all resize-none`}
                                                />
                                            </div>
                                            {errors.reason && (
                                                <p className="mt-1 text-sm text-red-400 flex items-center gap-1">
                                                    <AlertCircle className="w-4 h-4" />
                                                    {errors.reason}
                                                </p>
                                            )}
                                        </div>

                                        {/* Submit Button */}
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full py-4 bg-emerald text-white font-bold text-lg rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg shadow-emerald/20"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                    {variant === 'cancellation' ? 'Processing Cancellation...' : 'Sending...'}
                                                </>
                                            ) : submitStatus === 'success' ? (
                                                <>
                                                    <CheckCircle className="w-5 h-5" />
                                                    {variant === 'cancellation' ? 'Cancellation Initiated' : 'Message Sent!'}
                                                </>
                                            ) : (
                                                <>
                                                    <Send className="w-5 h-5" />
                                                    {variant === 'cancellation' ? 'Confirm Cancellation' : 'Send Message'}
                                                </>
                                            )}
                                        </button>

                                        {/* Error Message */}
                                        {submitStatus === 'error' && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-2 text-red-400"
                                            >
                                                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                                <p className="text-sm">
                                                    Something went wrong. Please try again or email us directly at support@visist.ai
                                                </p>
                                            </motion.div>
                                        )}
                                    </form>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};

export default ContactFormModal;
